/**
 * Authentication related English language pack
 */
export default {
  title: 'Smart Logistics Platform',
  subtitle: 'Making International Logistics Simpler',

  login: {
    welcome: 'Welcome Back',
    description: 'Sign in to your account to continue using smart logistics services',
    passwordLogin: 'Password Login',
    codeLogin: 'Code Login',
    accountLabel: 'Email or Phone',
    phoneOrEmail: 'Phone or Email',
    accountPlaceholder: 'Enter email or phone number',
    phoneOrEmailPlaceholder: 'Enter phone number or email',
    accountRequired: 'Please enter account information',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    passwordRequired: 'Please enter password',
    codeLabel: 'Verification Code',
    codePlaceholder: 'Enter verification code',
    codeRequired: 'Please enter verification code',
    sendCode: 'Send Code',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot Password?',
    changePassword: 'Change Password',
    loginButton: 'Sign In',
    loggingIn: 'Signing In...',
    // Messages
    checkInputMessage: 'Please check your input',
    passwordRequiredMessage: 'Please enter password',
    codeRequiredMessage: 'Please enter verification code',
    loginSuccessMessage: 'Login successful! Welcome back 🎉',
    loginFailedMessage: 'Login failed, please try again'
  },

  register: {
    title: 'Create Account',
    description: 'Start your smart logistics journey',
    usernameLabel: 'Username',
    usernamePlaceholder: 'Enter username',
    usernameRequired: 'Please enter username',
    phoneLabel: 'Phone Number',
    phonePlaceholder: 'Enter phone number',
    phoneRequired: 'Please enter phone number',
    phoneCodeLabel: 'Phone Verification Code',
    phoneCodePlaceholder: 'Enter verification code',
    phoneCodeRequired: 'Please enter phone verification code',
    sendCode: 'Send Code',
    emailLabel: 'Email (Optional)',
    emailPlaceholder: 'Enter email (optional)',
    passwordLabel: 'Set Password',
    passwordPlaceholder: 'At least 6 characters',
    confirmPasswordLabel: 'Confirm Password',
    confirmPasswordPlaceholder: 'Enter password again',
    agreementPrefix: 'I have read and agree to the',
    userAgreement: 'User Agreement',
    and: 'and',
    privacyPolicy: 'Privacy Policy',
    createButton: 'Create Account',
    creating: 'Creating...',
    // Messages
    fillAllFieldsMessage: 'Please fill in all required fields',
    passwordMismatchMessage: 'The two passwords do not match',
    registerSuccessMessage: 'Registration successful! Welcome to join us 🌟',
    registerSuccessLoginMessage: 'Registration successful, please log in',
    registerFailedMessage: 'Registration failed, please try again'
  },

  switch: {
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    register: 'Sign Up Now',
    login: 'Sign In Now'
  },

  decoration: {
    globalNetwork: 'Global Logistics Network',
    networkDescription: 'Connecting global ports, providing end-to-end logistics solutions',
    smartManagement: 'Smart Management',
    smartDescription:
      'AI-driven intelligent logistics management system to improve efficiency and reduce costs'
  },

  agreement: {
    title: 'User Agreement',
    subtitle: 'Smart Logistics Platform Terms of Service',
    understood: 'I have read and understood',
    section1: {
      title: '1. Acceptance of Terms',
      content:
        'Welcome to Smart Logistics Platform! This agreement is a legal agreement between you and this platform regarding the use of logistics services. By registering, accessing or using this service, you agree to accept all terms and conditions of this agreement.'
    },
    section2: {
      title: '2. Logistics Service Content',
      content:
        'This platform provides international logistics, freight forwarding, warehouse management, supply chain optimization and other services. Specific functions are subject to the actual provision of the platform. We are committed to providing customers with efficient and safe logistics solutions.'
    },
    section3: {
      title: '3. User Obligations and Responsibilities',
      content:
        'You promise to provide true and accurate cargo information and contact information, and update them in time. You are responsible for ensuring the legality of goods, complying with relevant import and export regulations, and cooperating with customs inspections and other related procedures.'
    },
    section4: {
      title: '4. Service Fees and Settlement',
      content:
        'Logistics service fees will be determined based on factors such as cargo weight, volume, transportation distance, and service type. Fee standards are transparent and open, supporting multiple settlement methods to ensure transaction security.'
    },
    section5: {
      title: '5. Disclaimer',
      content:
        'Within the scope permitted by law, the platform is not responsible for delays or losses caused by force majeure, policy changes, customs detention and other factors. It is recommended that customers purchase corresponding cargo insurance.'
    }
  },

  privacy: {
    title: 'Privacy Policy',
    subtitle: 'How We Protect Your Personal Information',
    understood: 'I have read and understood',
    section1: {
      title: '1. Information Collection and Use',
      content:
        'We collect your contact information, cargo details, transaction records, etc., which are used to provide logistics services, optimize user experience, and ensure transaction security. All information is strictly confidential and will not be used for other commercial purposes.'
    },
    section2: {
      title: '2. Data Security Protection',
      content:
        'We adopt bank-level data encryption technology, establish multiple security protection systems, and conduct regular security audits to ensure the security of your personal information and transaction data.'
    },
    section3: {
      title: '3. Third-party Cooperation',
      content:
        'In order to complete logistics services, we may share necessary information with third-party organizations such as carriers, customs, and ports. All partners have signed confidentiality agreements to ensure information security.'
    },
    section4: {
      title: '4. Data Storage and Cross-border Transfer',
      content:
        'Your data is mainly stored on secure servers within China. For international logistics business, cross-border data transmission may be involved, and we will comply with relevant legal and regulatory requirements.'
    },
    section5: {
      title: '5. Your Rights',
      content:
        'You have the right to access, correct, delete personal information, withdraw consent, and request data portability. If you need to exercise these rights or have any privacy questions, please contact our customer service team.'
    }
  },

  // Staff authentication specific
  staff: {
    title: 'Control Tower System',
    subtitle: 'Staff Management Platform',
    backToPortal: 'Back to Home',

    login: {
      title: 'Staff Login',
      description: 'Use your staff account to access the Control Tower System',
      quickLogin: 'Quick Login',
      or: 'or sign in with account',
      passwordLogin: 'Password Login',
      codeLogin: 'Code Login',
      accountLabel: 'Email or Phone',
      accountPlaceholder: 'Enter email or phone number',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      codeLabel: 'Verification Code',
      codePlaceholder: 'Enter verification code',
      sendCode: 'Send Code',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot Password?',
      loginButton: 'Sign In',
      loggingIn: 'Signing In...'
    },

    register: {
      title: 'Staff Registration',
      description: 'Create a staff account to join the Control Tower System',
      nameLabel: 'Staff Name',
      namePlaceholder: 'Enter staff name',
      phoneLabel: 'Phone Number',
      phonePlaceholder: 'Enter phone number',
      phoneCodeLabel: 'Phone Verification Code',
      phoneCodePlaceholder: 'Enter verification code',
      sendCode: 'Send Code',
      emailLabel: 'Corporate Email',
      emailPlaceholder: 'Enter corporate email',
      passwordLabel: 'Set Password',
      passwordPlaceholder: 'At least 6 characters',
      confirmPasswordLabel: 'Confirm Password',
      confirmPasswordPlaceholder: 'Enter password again',
      createButton: 'Create Account',
      creating: 'Creating...'
    },

    switch: {
      noAccount: "Don't have a staff account?",
      hasAccount: 'Already have a staff account?',
      register: 'Sign Up Now',
      login: 'Sign In Now'
    },

    decoration: {
      title: 'Control Tower System',
      description:
        'Centralized management, real-time monitoring, intelligent decision-making enterprise platform',
      staffTitle: 'Staff Zone',
      staffDescription:
        'Professional logistics management platform designed specifically for enterprise employees'
    },

    messages: {
      loginSuccess: 'Staff login successful! Welcome to Control Tower System 🎉',
      loginFailed: 'Login failed, please check your account and password',
      registerSuccess: 'Registration successful! Please login to your account',
      registerNotAvailable: 'Registration is currently unavailable, please contact administrator',
      codeSent: 'Verification code sent 📱',
      enterAccountFirst: 'Please enter email or phone number first',
      ssoRedirect: 'Redirecting to {provider} login',
      imageLoadError: 'Image loading failed'
    },

    validation: {
      accountRequired: 'Please enter email or phone number',
      passwordRequired: 'Please enter password',
      codeRequired: 'Please enter verification code',
      nameRequired: 'Please enter staff name',
      phoneRequired: 'Please enter phone number',
      phoneInvalid: 'Please enter a valid phone number',
      emailRequired: 'Please enter corporate email',
      emailInvalid: 'Please enter a valid email format',
      passwordMinLength: 'Password must be at least 6 characters',
      confirmPasswordRequired: 'Please confirm password',
      passwordMismatch: 'The two passwords do not match'
    },

    // Password reset modal
    resetPassword: {
      title: '🔑 Change Password',
      description: 'For your account security, please enter your account information and new password. The verification code will be sent to your phone or email.',
      accountLabel: 'Account Information',
      accountPlaceholder: 'Enter phone number or email address',
      accountRequired: 'Please enter phone number or email',
      codeLabel: 'Verification Code',
      codePlaceholder: 'Enter 6-digit verification code',
      codeRequired: 'Please enter verification code',
      sendCode: 'Send Code',
      resendAfter: 's to resend',
      newPasswordLabel: 'New Password',
      newPasswordPlaceholder: 'Enter new password (at least 6 characters)',
      newPasswordRequired: 'Please enter new password',
      passwordMinLength: 'Password must be at least 6 characters',
      confirmPasswordLabel: 'Confirm New Password',
      confirmPasswordPlaceholder: 'Enter new password again',
      confirmPasswordRequired: 'Please confirm new password',
      submitButton: 'Confirm Change',
      cancelButton: 'Cancel',
      successMessage: 'Password reset successful!',
      failedMessage: 'Password reset failed, please try again',
      codeSentMessage: 'Verification code sent 📱',
      codeFailedMessage: 'Failed to send verification code, please try again',
      enterAccountFirst: 'Please enter account first',
      enterPhoneFirst: 'Please enter phone number first',
      enterValidPhone: 'Please enter a valid phone number'
    }
  },

  // Forgot Password Modal
  forgotPassword: {
    title: 'Change Password',
    description:
      'For your account security, please enter your account information and new password. The verification code will be sent to your phone or email.',
    accountLabel: 'Account Information',
    accountPlaceholder: 'Enter phone number or email address',
    accountRequired: 'Please enter phone number or email',
    codeLabel: 'SMS Verification Code',
    codePlaceholder: 'Enter 6-digit verification code',
    codeRequired: 'Please enter verification code',
    sendCode: 'Send Code',
    resendCode: 'Resend',
    newPasswordLabel: 'New Password',
    newPasswordPlaceholder: 'Enter new password (at least 6 characters)',
    newPasswordRequired: 'Please enter new password',
    confirmPasswordLabel: 'Confirm New Password',
    confirmPasswordPlaceholder: 'Enter new password again',
    confirmPasswordRequired: 'Please confirm new password',
    passwordMismatch: 'The two passwords do not match',
    submitButton: 'Confirm Change',
    cancelButton: 'Cancel',
    successMessage: 'Password changed successfully! Please log in again 🎉',
    failedMessage: 'Password change failed, please try again',
    codeSentMessage: 'Verification code sent 📱',
    codeFailedMessage: 'Failed to send verification code, please try again'
  },

  // Store related messages
  store: {
    loginStart: 'Starting login authentication',
    passwordLoginStart: 'Password login',
    codeLoginStart: 'Code login',
    loginSuccess: 'Login successful',
    loginFailed: 'Login failed',
    registerCodeSent: 'Sending registration verification code',
    registerCodeFailed: 'Failed to send registration verification code',
    registerStart: 'User registration',
    registerSuccess: 'Registration successful',
    registerFailed: 'Registration failed',
    loginCodeSent: 'Sending login verification code',
    loginCodeFailed: 'Failed to send login verification code',
    resetCodeSent: 'Sending password reset verification code',
    resetCodeFailed: 'Failed to send password reset verification code',
    resetPasswordStart: 'Reset password',
    resetPasswordSuccess: 'Password reset successful',
    resetPasswordFailed: 'Password reset failed',
    logoutStart: 'Starting logout',
    logoutSuccess: 'Logout successful',
    logoutFailed: 'Logout failed',
    tokenStored: 'Token has been stored to both cookies and localStorage',
    authStateReset: 'Authentication state has been reset',
    tokenCleared: 'Token has been cleared from cookies and localStorage',
    tabsCacheCleared: 'Tabs cache has been cleared',
    userInfoUpdated: 'User information has been updated',
    authInitStart: 'Initializing authentication state...',
    authRestored: 'Restoring login state from local storage',
    authRestoreSuccess: 'Authentication state restored successfully',
    authRestoreFailed: 'Authentication state check failed',
    noValidAuth: 'No valid login state'
  },

  // Password reset related
  resetPassword: {
    title: 'Reset Password',
    accountLabel: 'Account',
    accountPlaceholder: 'Enter phone/email/username',
    accountRequired: 'Please enter account',
    codeLabel: 'Verification Code',
    codePlaceholder: 'Enter verification code',
    codeRequired: 'Please enter verification code',
    sendCode: 'Send Code',
    newPasswordLabel: 'New Password',
    newPasswordPlaceholder: 'Enter new password',
    newPasswordRequired: 'Please enter new password',
    confirmPasswordLabel: 'Confirm Password',
    confirmPasswordPlaceholder: 'Enter new password again',
    confirmPasswordRequired: 'Please confirm new password',
    submitButton: 'Reset Password',
    cancelButton: 'Cancel',
    passwordMismatch: 'The two passwords do not match'
  },
  tenantSelect: {
    title: 'Select Tenant',
    desc: 'Your account belongs to multiple tenants, please select the tenant you want to log in to',
    label: 'Tenant List',
    cancel: 'Cancel',
    confirm: 'Confirm',
    error: 'Please select a tenant',
    selectPlaceholder: 'Please select a tenant'
  },

  // Tenant login related
  tenantLogin: {
    title: 'Smart Logistics Platform',
    subtitle: 'Making International Logistics Simpler',
    backToHome: 'Back to Home',

    // Login page
    login: {
      welcome: 'Welcome Back',
      description: 'Sign in to your account to continue using smart logistics services',
      globalNetwork: 'Global Logistics Network',
      networkDescription: 'Connecting global ports, providing end-to-end logistics solutions'
    },

    // Register page
    register: {
      title: 'Create Account',
      description: 'Start your smart logistics journey',
      joinUs: 'Join Us',
      joinDescription: 'Choice of tens of thousands of enterprises, start a new smart logistics experience'
    },

    // Switch links
    switch: {
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      register: 'Sign Up Now',
      login: 'Sign In Now'
    },

    // Tenant selection modal
    tenantModal: {
      title: 'Select Tenant',
      description: 'Your account belongs to multiple tenants, please select the tenant you want to log in to',
      label: 'Tenant List',
      personalAccount: 'Personal Account',
      cancel: 'Cancel',
      confirm: 'Confirm'
    },

    // Account exists reminder modal
    accountExists: {
      title: 'Account Reminder',
      description: 'The system detected that you may already have an account',
      mainTitle: 'Current Account Already Registered',
      content: 'It is detected that your account information already exists in the system and does not need to be registered repeatedly. Please log in directly with your existing account and enjoy our smart logistics services.',
      tipTitle: 'Friendly Reminder:',
      tipContent: 'If you have forgotten your login password, you can use the "Forgot Password" function to reset your password.',
      cancel: 'Cancel',
      goToLogin: 'Go to Login'
    },

    // Forgot password modal
    forgotPassword: {
      title: 'Change Password',
      description: 'For your account security, please enter your account information and new password',
      accountLabel: 'Account Information',
      accountPlaceholder: 'Enter phone number or email address',
      codeLabel: 'SMS Verification Code',
      codePlaceholder: 'Enter 6-digit verification code',
      sendCode: 'Send Code',
      resendAfter: 's to resend',
      newPasswordLabel: 'New Password',
      newPasswordPlaceholder: 'Enter new password (at least 6 characters)',
      confirmPasswordLabel: 'Confirm New Password',
      confirmPasswordPlaceholder: 'Enter new password again',
      cancel: 'Cancel',
      confirm: 'Confirm Change',
      resetting: 'Resetting...',
      successMessage: 'Password reset successful!',
      failedMessage: 'Password reset failed, please try again'
    },

    // Messages
    messages: {
      loginSuccess: 'Login successful! Welcome back 🎉',
      loginFailed: 'Login failed, please try again',
      registerSuccess: 'Registration successful! Welcome to join us 🌟',
      registerFailed: 'Registration failed, please try again',
      codeSent: 'Verification code sent 📱',
      codeFailed: 'Failed to send verification code, please try again',
      enterAccountFirst: 'Please enter phone number or email first',
      enterPhoneFirst: 'Please enter phone number first',
      enterValidPhone: 'Please enter a valid phone number',
      enterAccount: 'Please enter account first',
      passwordMismatch: 'The two passwords do not match',
      personalLoginSuccess: 'Login successful! Welcome to use personal account 👤',
      enterpriseLoginSuccess: 'Login successful! Welcome to enterprise control tower 🏢'
    }
  },

  // SSO authorization related
  sso: {
    backToLogin: 'Back to Login',
    back: 'Back',
    authLogin: 'Authorization Login',
    authDescription: 'Please use your {provider} account for authorization',
    usernameLabel: 'Username',
    usernamePlaceholder: 'Enter username',
    usernameRequired: 'Please enter username',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter password',
    passwordRequired: 'Please enter password',
    authButton: 'Authorize Login',
    authorizing: 'Authorizing...',
    authAgreement: 'By logging in, you agree to authorize the Control Tower system to access your {provider} account information',
    selectTenant: 'Select Tenant',
    selectTenantDesc: 'Please enter tenant name, English name or ID for search',
    tenantLabel: 'Tenant List',
    tenantPlaceholder: 'Enter tenant name, English name or ID',
    noMatchResult: 'No matching results',
    cancel: 'Cancel',
    confirmBind: 'Confirm Binding',
    selectTenantError: 'Please select a tenant',
    verifyUser: 'Verify User',
    verifyUserDesc: 'The login name already exists in the system. Please select a user and enter the password for verification',
    selectUserPlaceholder: 'Please select an account',
    loginSuccess: 'Login successful',
    loginFailed: 'Login failed, please try again',
    verifyLogin: 'Verify Login',
    passwordError: 'Passwords do not match'
  },

  // New: Login origin type enum
  enum: {
    loginOriginType: {
      WTF: 'walltech family',
      CARGO: 'CargoWare',
      ETOWER: 'eTower'
    }
  }
}
